<aside
  class="flex flex-col bg-white border-r border-n30 h-full relative overflow-visible"
>
  <!-- Logo + Client pill -->
  <div class="border-b border-n30">
    <div
      class="flex items-center"
      [ngClass]="
        isCollapsed
          ? 'justify-center px-2 py-4'
          : 'justify-between px-4 pt-4 pb-3'
      "
    >
      <img
        src="assets/images/logo/logo.png"
        alt="MobileWallet"
        class="w-auto"
        [style.height]="isCollapsed ? '32px' : '44px'"
      />
      <!-- Mobile close button -->
      <button
        (click)="close.emit()"
        class="lg:hidden p-1 text-n400 hover:text-n700"
        aria-label="Close menu"
      >
        <i class="ph ph-x text-xl"></i>
      </button>
    </div>

    <!-- Client pill (hidden when collapsed) -->
    <div *ngIf="!isCollapsed" class="px-3 pb-3">
      <div
        class="flex items-center gap-2.5 rounded-xl bg-b50 border border-b100 px-3 py-2 hover:bg-b100 transition-colors"
      >
        <div
          class="flex-shrink-0 w-7 h-7 rounded-lg bg-b300 flex items-center justify-center"
        >
          <i class="ph ph-bold ph-lightning text-white text-xs"></i>
        </div>
        <div class="min-w-0">
          <p
            class="text-[10px] text-n400 leading-none mb-0.5 uppercase tracking-wide font-medium"
          >
            Client
          </p>
          <p class="text-sm font-bold text-b300 leading-tight truncate">
            Bluecore
          </p>
        </div>
        <i
          class="ph ph-arrow-up-right text-n400 text-xs ml-auto flex-shrink-0"
        ></i>
      </div>
    </div>
  </div>

  <!-- Navigation -->
  <nav class="flex-1 overflow-y-auto py-3 px-2">
    <!-- Collapsed: flat icon list with group dividers -->
    <ng-container *ngIf="isCollapsed">
      <div class="flex flex-col gap-0.5">
        <ng-container *ngFor="let group of visibleGroups; let gi = index">
          <hr *ngIf="gi > 0" class="border-n20 my-2 mx-1" />
          <a
            *ngFor="let item of group.items"
            [routerLink]="item.url"
            [title]="item.label"
            class="flex items-center justify-center py-2.5 rounded-xl transition-colors"
            [ngClass]="
              isItemActive(item.url)
                ? 'bg-b300 text-white'
                : 'text-n500 hover:bg-b50 hover:text-b300'
            "
          >
            <i [class]="'ph ph-bold ' + item.icon + ' text-lg'"></i>
          </a>
        </ng-container>
      </div>
    </ng-container>

    <!-- Expanded: grouped with collapsible headers -->
    <ng-container *ngIf="!isCollapsed">
      <div class="flex flex-col gap-1">
        <ng-container *ngFor="let group of visibleGroups">
          <div>
            <!-- Group header -->
            <button
              (click)="toggleGroup(group.key)"
              class="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
              [ngClass]="
                isGroupActive(group) ? 'text-b300' : 'text-n400 hover:text-n700'
              "
            >
              <i
                [class]="'ph ph-bold ' + group.icon + ' text-sm flex-shrink-0'"
              ></i>
              <span
                class="text-xs font-semibold uppercase tracking-wider flex-1 text-left"
                >{{ group.label }}</span
              >
              <i
                class="ph ph-caret-right text-xs transition-transform duration-200"
                [class.rotate-90]="activeGroup === group.key"
              ></i>
            </button>

            <!-- Items (visible when group is open) -->
            <div
              *ngIf="activeGroup === group.key"
              class="ml-3 pl-3 border-l border-n30 mt-0.5 mb-1 flex flex-col gap-0.5"
            >
              <a
                *ngFor="let item of group.items"
                [routerLink]="item.url"
                class="flex items-center gap-3 px-3 py-2 rounded-xl transition-colors"
                [ngClass]="
                  isItemActive(item.url)
                    ? 'bg-b300 text-white'
                    : 'text-n500 hover:bg-b50 hover:text-b300'
                "
              >
                <i [class]="'ph ' + item.icon + ' text-base flex-shrink-0'"></i>
                <span class="text-sm font-medium truncate">{{
                  item.label
                }}</span>
              </a>
            </div>
          </div>
        </ng-container>
      </div>
    </ng-container>
  </nav>

  <!-- User profile + actions -->
  <div class="border-t border-n30 px-3 py-3">
    <div *ngIf="!isCollapsed" class="flex items-center gap-3 mb-2">
      <!-- Avatar -->
      <div
        class="w-8 h-8 rounded-full bg-b300 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
      >
        {{ userInitials }}
      </div>
      <!-- Name + role -->
      <div class="min-w-0 flex-1" *ngIf="currentUser">
        <p class="text-sm font-semibold text-n900 leading-none m-0 truncate">
          {{ currentUser.firstName }} {{ currentUser.lastName }}
        </p>
        <p class="text-xs text-n400 mt-0.5 m-0">{{ currentUser.role }}</p>
      </div>
      <!-- Actions: security + notifications + logout -->
      <div class="flex items-center gap-1 flex-shrink-0">
        <a
          routerLink="/dashboard/security"
          (click)="close.emit()"
          class="flex items-center justify-center w-8 h-8 rounded-xl text-n400 hover:bg-n20 hover:text-b300 transition-colors"
          ngbTooltip="Security"
          placement="top"
        >
          <i class="ph ph-lock-key text-base"></i>
        </a>
        <a
          routerLink="/dashboard/notifications"
          (click)="close.emit()"
          class="flex items-center justify-center w-8 h-8 rounded-xl text-n400 hover:bg-n20 hover:text-b300 transition-colors"
          ngbTooltip="Notifications"
          placement="top"
        >
          <i class="ph ph-bell text-base"></i>
        </a>
        <button
          (click)="logout()"
          class="flex items-center justify-center w-8 h-8 rounded-xl text-n400 hover:bg-r50 hover:text-r300 transition-colors"
          ngbTooltip="Logout"
          placement="top"
        >
          <i class="ph ph-sign-out text-base"></i>
        </button>
      </div>
    </div>
    <!-- Collapsed: just icon buttons -->
    <div *ngIf="isCollapsed" class="flex flex-col items-center gap-1">
      <div
        class="w-8 h-8 rounded-full bg-b300 flex items-center justify-center text-white text-xs font-semibold"
      >
        {{ userInitials }}
      </div>
      <a
        routerLink="/dashboard/notifications"
        class="flex items-center justify-center w-8 h-8 rounded-xl text-n400 hover:bg-n20 hover:text-b300 transition-colors"
        ngbTooltip="Notifications"
        placement="right"
      >
        <i class="ph ph-bell text-base"></i>
      </a>
      <button
        (click)="logout()"
        class="flex items-center justify-center w-8 h-8 rounded-xl text-n400 hover:bg-r50 hover:text-r300 transition-colors"
        ngbTooltip="Logout"
        placement="right"
      >
        <i class="ph ph-sign-out text-base"></i>
      </button>
    </div>
  </div>

  <!-- Collapse toggle — desktop only -->
  <div class="hidden lg:block px-2 pb-4 pt-4 border-t border-n30">
    <button
      (click)="isCollapsed = !isCollapsed"
      class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-n400 hover:bg-n20 hover:text-n700 transition-colors"
      [attr.aria-label]="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
    >
      <i
        [class]="
          'ph ph-bold text-base ' +
          (isCollapsed ? 'ph-arrow-right' : 'ph-arrow-left')
        "
      ></i>
      <span *ngIf="!isCollapsed" class="text-xs font-medium">Collapse</span>
    </button>
  </div>

  <!-- Floating expand tab — only visible when collapsed on desktop -->
  <button
    *ngIf="isCollapsed"
    (click)="isCollapsed = false"
    aria-label="Expand sidebar"
    class="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-12 items-center justify-center rounded-r-lg bg-white border border-l-0 border-n30 text-n400 hover:text-b300 hover:bg-b50 transition-colors shadow-sm"
  >
    <i class="ph ph-bold ph-caret-right text-xs"></i>
  </button>
</aside>
